// Races child process exit against the startup/deadline windows (both
// measured from t0, per RuntimeLimits — no bare numeric literals). Split out
// of session-runner.ts to keep that file under the 200-line budget.
//
// The poll tick always yields a real event-loop turn (`setImmediate`)
// BEFORE optionally calling the injected `deps.sleep` — this guarantees
// `child.exit`'s listener (a real Node event, delivered on a macrotask) gets
// a chance to fire between polls even if a test stubs `deps.sleep` as an
// immediately-resolving no-op (`async () => {}`, a pure microtask that on
// its own would starve macrotasks in a tight self-rescheduling loop).
// `deps.sleep` itself remains the source of truth for how long to wait
// between polls — tests that care about deadline timing precision advance
// their fake clock inside their `sleep` stub, same as before.
import type { ChildProcess } from "node:child_process";
import type { RuntimeLimits } from "./runtime-limits-from-effective-config.js";

export interface ExitInfo {
  kind: "exited" | "startup_timeout" | "deadline";
  code: number | null;
  signal: NodeJS.Signals | null;
}

export interface ExitWaiterDeps {
  now: () => number;
  sleep: (ms: number) => Promise<void>;
}

const POLL_INTERVAL_CAP_MS = 50;

function yieldEventLoopTurn(): Promise<void> {
  return new Promise((resolve) => setImmediate(resolve));
}

export function waitForExitOrDeadline(
  child: ChildProcess,
  deps: ExitWaiterDeps,
  t0: number,
  limits: RuntimeLimits,
  hasSeenInit: () => boolean,
): Promise<ExitInfo> {
  return new Promise<ExitInfo>((resolve) => {
    let settled = false;
    const finish = (info: ExitInfo) => {
      if (settled) return;
      settled = true;
      resolve(info);
    };

    child.once("exit", (code, signal) => finish({ kind: "exited", code, signal }));

    void (async () => {
      while (!settled) {
        const elapsed = deps.now() - t0;
        if (!hasSeenInit() && elapsed >= limits.startupTimeoutMs) {
          finish({ kind: "startup_timeout", code: null, signal: null });
          return;
        }
        if (elapsed >= limits.deadlineMs) {
          finish({ kind: "deadline", code: null, signal: null });
          return;
        }
        await yieldEventLoopTurn();
        if (settled) return;
        await deps.sleep(Math.min(POLL_INTERVAL_CAP_MS, limits.startupTimeoutMs));
      }
    })();
  });
}
